import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { RadioGroup } from "@headlessui/react";

import useAccount from "../../data/useAccount";
import classNames from "../../utils/classNames";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import AccountDetails from "../../components/accounts/AccountDetails";
import AccountAddressList from "./../../components/accounts/AccountAddressList";
import AccountContactList from "./../../components/accounts/AccountContactList";
import AccountInvoiceList from "../../components/accounts/AccountInvoiceList";
import AccountLeadsList from "../../components/accounts/AccountLeadList";
import AccountNotes from "../../components/accounts/AccountNotes";
import AccountPartCategories from "../../components/accounts/AccountPartCategories";
import AccountWasteReceiptList from "../../components/accounts/AccountWasteReceiptList";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

export default function Account() {
  const { accountId } = useParams();
  const { account } = useAccount(accountId, "Account");
  const { setInfo } = usePageNameContext();
  const [activeTab, setActiveTab] = useState(0);
  const hasTme = CheckFeatureFlag("TME");
  const hasDefra = CheckFeatureFlag("DEFRA");
  const isHaulier = hasDefra && !!account.data?.data?.isHaulier;

  const tabs = [
    { value: 0, label: "Contacts" },
    { value: 1, label: "Addresses" },
    { value: 2, label: "Lead History" },
    { value: 3, label: "Part Sales History" },
    { value: 4, label: "Notes" },
    ...(hasTme ? [{ value: 5, label: "Part Categories" }] : []),
    ...(isHaulier ? [{ value: 6, label: "Waste Movements" }] : []),
  ];

  // Tailwind can't build the class from a number at runtime.
  const tabGridCols: Record<number, string> = {
    5: "md:grid-cols-5",
    6: "md:grid-cols-6",
    7: "md:grid-cols-7",
  };

  useEffect(() => {
    if (!account.isLoading && !account.isError) {
      setInfo({
        name: `${account.data?.data?.name}`,
        desc: "View and manage the account record here",
      });
    }
  }, [account.isLoading, account.isError, account.data]);

  if (account.isLoading) {
    return (
      <div className="min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  } else if (account.isError || !account.data || !account.data.data) {
    return (
      <p className="text-lg text-red-600 font-medium">
        An error occurred retrieving account
      </p>
    );
  } else {
    return (
      <div className="mt-8">
        <div>
          <AccountDetails account={account.data.data} />
        </div>
        <div>
          <RadioGroup
            value={activeTab}
            onChange={setActiveTab}
            className="my-4"
          >
            <RadioGroup.Label className="sr-only">
              Choose which view to display
            </RadioGroup.Label>
            <div
              className={classNames(
                "grid grid-cols-2 gap-3",
                tabGridCols[tabs.length] ?? "md:grid-cols-5"
              )}
            >
              {tabs.map((tab) => (
                <RadioGroup.Option
                  key={`${tab.value}`}
                  value={tab.value}
                  className={({ active, checked }) =>
                    classNames(
                      active ? "ring-2 ring-offset-2 ring-primary-500" : "",
                      checked
                        ? "bg-primary-600 border-transparent text-white hover:bg-primary-700"
                        : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50",
                      "cursor-pointer focus:outline-none border rounded-md py-1.5 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                    )
                  }
                >
                  <RadioGroup.Label as="span">{tab.label}</RadioGroup.Label>
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>
        </div>
        <div className="bg-white shadow rounded-lg mt-4 overflow-hidden mb-24">
          {
            {
              0: <AccountContactList account={account.data.data} />,
              1: <AccountAddressList account={account.data.data} />,
              2: <AccountLeadsList account={account.data.data} />,
              3: <AccountInvoiceList account={account.data.data} />,
              4: <AccountNotes notes={account.data.data.accountNotes ?? []} account={account.data.data} />,
              5: <AccountPartCategories account={account.data.data} />,
              6: <AccountWasteReceiptList account={account.data.data} />,
            }[activeTab]
          }
        </div>
      </div>
    );
  }
}
